import isEmpty from "lodash-es/isEmpty";
import isNil from "lodash-es/isNil";
import type { Session } from "../../common/types/companyTypes";
import {
   createSameTabRefresh,
   exchangeBootstrapCodeWithCookie,
   loginSessionWithCookie,
   logoutSessionWithCookie,
   refreshSessionWithCookie,
   verifySmsLoginCodeWithCookie
} from "./authSessionJwtCookie";
import type { LoginSessionParams, VerifySmsLoginParams } from "./authSessionTypes";
import { calculateTokenRefreshDelay } from "./authTokenTiming";

type LoginAuthSessionParams = {
   loginEndpoint: string;
   params: LoginSessionParams;
};

type VerifySmsAuthSessionParams = {
   verifyEndpoint: string;
   params: VerifySmsLoginParams;
};

/**
 * Auth-related query params that must be stripped from the URL once a handoff has been attempted,
 * so credentials don't linger in the address bar, history, or referrers.
 */
export const consumableAuthQueryParams = (params: URLSearchParams): string[] =>
   ["bootstrapCode", ...["token", "expiry"]].filter((param) => params.has(param));

const refreshAccessTokenOnce = createSameTabRefresh();

export const loginAuthSession = ({ loginEndpoint, params }: LoginAuthSessionParams): Promise<Session> =>
   loginSessionWithCookie(loginEndpoint, params);

export const verifySmsAuthSession = ({ verifyEndpoint, params }: VerifySmsAuthSessionParams): Promise<Session> =>
   verifySmsLoginCodeWithCookie(verifyEndpoint, params);

export const refreshAuthSession = (refreshEndpoint: string, onSession: (newSession: Session) => void): Promise<Session> =>
   refreshAccessTokenOnce(() => refreshSessionWithCookie(refreshEndpoint), onSession);

export const logoutAuthSession = (logoutEndpoint: string, session: Session): Promise<unknown> =>
   logoutSessionWithCookie(logoutEndpoint, session.accessToken);

export const calculateAuthSessionRefreshDelay = (expiryIn: number): number => calculateTokenRefreshDelay(expiryIn);

export const resolveAuthSessionFromQuery = async (
   params: URLSearchParams,
   resolveBootstrapEndpoint: () => string
): Promise<Session | null> => {
   const bootstrapCode = params.get("bootstrapCode");
   if (!isNil(bootstrapCode) && !isEmpty(bootstrapCode)) {
      return exchangeBootstrapCodeWithCookie(resolveBootstrapEndpoint(), bootstrapCode);
   }

   return null;
};
